import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoBayrak } from './FikoBayrak';
import * as THREE from 'three';

/**
 * 🚩 CATIA-style Flag Marker Dialog
 */
@Component({
  selector: 'app-fiko-bayrak',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-bayrak.component.html',
  styleUrls: ['./fiko-bayrak.component.css']
})
export class FikoBayrakComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // Flag settings
  metin: string = '';
  renk: string = '#FF4444';
  kutupYuksekligi: number = 1.5;
  bayrakGenisligi: number = 0.8;
  bayrakYuksekligi: number = 0.5;
  boyut: number = 12;

  // Point selection
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  // Current annotation
  mevcutBayrak: FikoBayrak | null = null;

  // Predefined colors
  renkler = ['#FF4444', '#FFD700', '#00FF00', '#00FFFF', '#FF00FF', '#FFA500'];

  // Sabit boyut - kamera zoom'una göre scale ayarlanır
  sabitBoyut: boolean = true;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  bayrakAyarlariAcik = true;
  gorunumAyarlariAcik = false;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutBayrak) {
      this.mevcutBayrak.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutBayrak) {
      sahne.remove(this.mevcutBayrak);
      this.mevcutBayrak.dispose();
    }

    // Yeni önizleme oluştur
    this.mevcutBayrak = new FikoBayrak(
      sahne,
      this.nokta,
      this.metin,
      this.renk
    );

    sahne.add(this.mevcutBayrak);
    this.anaGosterim?.ciz();
  }

  /**
   * Add flag to scene
   */
  ekle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir nokta seçin!');
      return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    // Create flag
    const bayrak = new FikoBayrak(
      sahne,
      this.nokta,
      this.metin,
      this.renk
    );

    // Add to etiketler group
    const etiket = sahne.fikoEtiketler.ekleBayrak();
    etiket.userData['FikoAnnotation'] = bayrak;
    bayrak.parent = etiket;
    sahne.add(bayrak);

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.nokta = null;
    if (this.mevcutBayrak) {
      sahne.remove(this.mevcutBayrak);
      this.mevcutBayrak = null;
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutBayrak) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutBayrak);
        this.mevcutBayrak.dispose();
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }
}
